import { type ReactNode } from 'react';

import { canOfferAct, type Session } from '@/lib/permissions';

/*
 * ActGate (composed; E01.F03b.R60 — AC-93489b84, AC-6c3226b8).
 *
 * The one domain-free courtesy gate a surface wraps an act-control in. Given the
 * resolved session and the single act a control performs, it renders the control
 * only when a permission set the person holds covers that act, reading the
 * server-sourced effective permissions through `canOfferAct` (lib/permissions,
 * the R55 per-act predicate applied per surface). Because every surface — a
 * list, a detail page, a search result — wraps its control in this same gate,
 * the withholding is decided in exactly one place and holds "at every surface it
 * presents rather than on a single screen" (AC-6c3226b8); none can drift.
 *
 * Domain-free: data via props, never a store read or a fetch (AGENTS.md
 * components/composed rule). The consuming feature passes the session and the
 * act. It is the COURTESY half only — a withheld control is never the
 * enforcement; the domain gate refuses the act on the request whether or not it
 * was ever offered (AC-8b85b662), and the frontend backstop is the 401/403
 * interceptor in app/query-client.ts.
 *
 * A withheld act renders nothing — nothing in the tree, so there is nothing to
 * announce (OQ-E01-04, Rick Barker 2026-10-05: a hidden control surfaces no
 * reason; the generic refusal is announced only on an attempted-and-refused
 * act).
 */
export function ActGate({
  session,
  act,
  children,
}: {
  session: Session | null;
  act: string;
  children: ReactNode;
}) {
  if (!canOfferAct(session, act)) return null;
  return <>{children}</>;
}
